import { useCallback, useEffect, useState } from 'react';
import { knowledgeAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, FolderOpen, RefreshCw } from 'lucide-react';

export default function KBNavigationPanel({ user }) {
  const [nav, setNav] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subpath, setSubpath] = useState('');
  const [rootType, setRootType] = useState('');
  const [rootId, setRootId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (subpath) params.set('subpath', subpath);
      if (rootType) params.set('root_type', rootType);
      if (rootId) params.set('root_id', rootId);
      const res = await knowledgeAPI.navigation(params.toString() ? `?${params}` : '');
      setNav(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [subpath, rootType, rootId]);

  useEffect(() => { load(); }, [load]);

  const downloadUrl = (entry) => {
    const p = new URLSearchParams({
      source_type: entry.source_type,
      rel_path: entry.rel_path,
    });
    if (entry.repo_id) p.set('repo_id', entry.repo_id);
    if (entry.mount_id) p.set('mount_id', entry.mount_id);
    if (entry.capsule_id) p.set('capsule_id', entry.capsule_id);
    return `/api/knowledge/download?${p}`;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <FolderOpen className="w-4 h-4" /> Navegacion KB
        </CardTitle>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className="w-3 h-3 mr-1" /> Actualizar
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-zinc-500">
          {nav?.scope?.is_admin
            ? 'Vista administrador: todos los origenes del tenant.'
            : `Capsulas visibles: ${(nav?.scope?.allowed_capsules || []).join(', ') || 'ninguna'}`}
        </p>
        <div className="flex flex-wrap gap-2">
          {(nav?.roots || []).map((r) => (
            <Button
              key={`${r.type}-${r.id}`}
              size="sm"
              variant={rootType === r.type && rootId === r.id ? 'default' : 'outline'}
              className="text-xs"
              onClick={() => {
                setRootType(r.type);
                setRootId(r.id || '');
                setSubpath('');
              }}
            >
              {r.label || r.id}
            </Button>
          ))}
          <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setRootType(''); setRootId(''); setSubpath(''); }}>
            Todos
          </Button>
        </div>
        {loading ? (
          <p className="text-sm text-zinc-400">Cargando...</p>
        ) : (
          <div className="border rounded-sm max-h-[420px] overflow-auto text-xs font-mono">
            {(nav?.entries || []).length === 0 ? (
              <p className="p-4 text-zinc-400">Sin archivos en este scope.</p>
            ) : (
              <table className="w-full">
                <tbody>
                  {nav.entries.map((e) => (
                    <tr key={`${e.source_type}-${e.rel_path}`} className="border-b hover:bg-zinc-50">
                      <td className="p-2">{e.rel_path}</td>
                      <td className="p-2 text-zinc-500">{Math.round((e.size_bytes || 0) / 1024)} KB</td>
                      <td className="p-2">
                        <a href={downloadUrl(e)} target="_blank" rel="noreferrer" className="inline-flex items-center text-brand-primary">
                          <Download className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
